import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContext';
import { useContracts } from '../hooks/useContract';
import { useXtPrice } from '../hooks/useXtPrice';
import { useNotify } from '../context/NotificationContext';
import { CONTRACTS } from '../config/contracts';
import { CURRENT_NETWORK } from '../config/networks';
import { formatUSDT } from '../utils/format';
import { PageLoader } from '../components/common/Loader';
import './XTTokenPage.css';

const WHITEPAPER_URL = '/whitepaper.pdf';
const PLAN_URL = '/plan.pdf';

const UTILITY = [
  { icon: '⛏', title: 'Mining Rewards',  text: 'Earn XT daily by mining with your XTerra Forge machines.' },
  { icon: '💱', title: 'USDT Swap',       text: 'Convert XT to USDT during withdrawal at the platform rate.' },
  { icon: '🎁', title: 'Level Rewards',   text: 'Earn a share of every purchase in your 12-level downline.' },
  { icon: '🔐', title: 'Non-Custodial',   text: 'You hold your XT — every reward lands in your own wallet.' },
  { icon: '🌐', title: 'Ecosystem Fuel',  text: 'Foundation of the future X-Terra Blockchain and dApps.' },
  { icon: '📈', title: 'Deflationary',    text: 'Users can burn XT — supply reduces over time.' },
];

const TOKENOMICS = [
  { label: 'Symbol',        value: 'XT' },
  { label: 'Standard',      value: 'BEP-20' },
  { label: 'Decimals',      value: '18' },
  { label: 'Chain',         value: 'BNB Chain' },
  { label: 'Max Supply',    value: '1,000,000,000' },
  { label: 'Mining Reward', value: 'Daily mining cycles' },
];

const HOW_TO_EARN = [
  { title: 'Register',         text: 'Create your account with a sponsor ID (or use the default).' },
  { title: 'Purchase a Forge', text: 'Buy an XTerra Forge machine to unlock daily mining rewards.' },
  { title: 'Harvest Daily',    text: 'Click Harvest every cycle — mining rewards accumulate in your balance.' },
  { title: 'Grow Your Team',   text: 'Share your referral link to earn multi-level rewards from your downline.' },
  { title: 'Unlock More Levels',text: 'The more qualifying directs you have, the more levels you unlock (up to 12).' },
  { title: 'Withdraw or Swap', text: 'Withdraw as XT or USDT any time — right from your dashboard.' },
];

const ROADMAP = [
  { phase: 'Phase 1',  title: 'BEP-20 Launch',        text: 'XT deployed on BNB Chain with mining platform live.', status: 'done' },
  { phase: 'Phase 2',  title: 'Ecosystem Expansion',  text: 'Community growth, referral network, staking dApps.', status: 'current' },
  { phase: 'Phase 3',  title: 'Exchange Listings',    text: 'Multi-DEX & CEX listings for XT / USDT trading.', status: 'upcoming' },
  { phase: 'Phase 4',  title: 'X-Terra Blockchain',   text: 'Launch of the native X-Terra L1 with XT as gas.', status: 'upcoming' },
  { phase: 'Phase 5',  title: 'Renewable Integration',text: 'Renewable-energy-backed utility & real-world assets.', status: 'upcoming' },
];

const FAQ = [
  { q: 'How do I earn XT?', a: 'Purchase an XTerra Forge machine, click Harvest daily, and XT is credited to your balance based on the machine\'s daily reward.' },
  { q: 'What is the XT price based on?', a: 'The XT price is determined by the platform based on the current ecosystem valuation and market conditions. The latest XT price is displayed at the top of the page and any price update will apply to future rewards and swaps.' },
  { q: 'Can I swap XT to USDT?', a: 'Yes — during withdrawal you can choose to receive USDT or XT. The system converts using the current XT price minus the platform fee.' },
  { q: 'What network is XT on?', a: 'XT is a BEP-20 token on BNB Chain. Connect any BEP-20 compatible wallet (Trust Wallet, MetaMask, Binance Wallet, etc.).' },
  { q: 'How is the whitepaper different from the platform docs?', a: 'The whitepaper covers the full technical architecture, tokenomics, and long-term vision. Read it before making any purchase decisions.' },
];

export default function XTTokenPage() {
  const { injected, isConnected } = useWallet();
  const { read } = useContracts();
  const { xtPrice, loading: priceLoading } = useXtPrice();
  const notify = useNotify();

  const [meta, setMeta] = useState({ name: '', symbol: '', decimals: 18, supply: null, loading: true });
  const [copied, setCopied] = useState(false);
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      read.xt.name(),
      read.xt.symbol(),
      read.xt.decimals(),
      read.xt.totalSupply(),
    ]).then(([n, s, d, sup]) => {
      if (cancelled) return;
      setMeta({
        name: n.status === 'fulfilled' ? n.value : 'X-Terra Token',
        symbol: s.status === 'fulfilled' ? s.value : 'XT',
        decimals: d.status === 'fulfilled' ? d.value : 18,
        supply: sup.status === 'fulfilled' ? sup.value : null,
        loading: false,
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function copyAddr() {
    if (!CONTRACTS.XT || CONTRACTS.XT === ethers.constants.AddressZero) return;
    navigator.clipboard.writeText(CONTRACTS.XT)
      .then(() => {
        setCopied(true);
        notify.success('XT contract address copied.');
        setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => notify.error('Could not copy.'));
  }

  async function addTokenToWallet() {
    if (!injected) return notify.error('Please connect your wallet first.');
    if (!CONTRACTS.XT || CONTRACTS.XT === ethers.constants.AddressZero) {
      return notify.error('XT contract address not configured yet.');
    }
    try {
      await injected.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: CONTRACTS.XT,
            symbol: meta.symbol || 'XT',
            decimals: meta.decimals || 18,
            image: window.location.origin + '/logos/xterra-icon.png',
          },
        },
      });
      notify.success('XT added to wallet.');
    } catch (err) {
      if (err && err.code !== 4001) notify.error(err.message || 'Could not add token.');
    }
  }

  const totalSupplyDisplay = meta.supply
    ? Number(ethers.utils.formatUnits(meta.supply, meta.decimals)).toLocaleString('en-US', { maximumFractionDigits: 0 })
    : '—';

  const isZeroAddr = !CONTRACTS.XT || CONTRACTS.XT === ethers.constants.AddressZero;

  if (meta.loading && priceLoading) {
    return (
      <div className="container xt-page">
        <PageLoader label="LOADING XT TOKEN" sub="Fetching on-chain data..." />
      </div>
    );
  }

  return (
    <div className="container xt-page">
      <div className="network-chip">{CURRENT_NETWORK.displayName} · Chain {CURRENT_NETWORK.chainId}</div>

      {/* HERO */}
      <div className="xt-hero">
        <div className="xt-hero-logo">
          <img src="/logos/xterra-icon.png" alt="XT" />
        </div>
        <div>
          <div className="xt-hero-eyebrow">Official Token</div>
          <h1>
            {meta.symbol || 'XT'} <span className="text-gradient">Token</span>
          </h1>
          <p className="subtitle">
            {meta.name || 'X-Terra Token'} powers the entire X-Terra mining ecosystem. Earned through
            daily mining, redeemable for USDT, and the utility asset for the future X-Terra Blockchain.
          </p>
          <div className="xt-hero-badges">
            <span className="xt-badge">🌐 <strong>{CURRENT_NETWORK.displayName}</strong></span>
            <span className="xt-badge">📜 <strong>BEP-20</strong></span>
            <span className="xt-badge">🔐 <strong>Non-Custodial</strong></span>
          </div>
        </div>
      </div>

      {/* STATS STRIP */}
      <div className="xt-stats-strip">
        <div className="xt-stat-cell">
          <div className="l">Current Price</div>
          <div className="v">{xtPrice ? formatUSDT(xtPrice, 4) : '—'}<span className="cur">USDT</span></div>
        </div>
        <div className="xt-stat-cell">
          <div className="l">Symbol</div>
          <div className="v">{meta.symbol || 'XT'}</div>
        </div>
        <div className="xt-stat-cell">
          <div className="l">Decimals</div>
          <div className="v">{meta.decimals || 18}</div>
        </div>
        <div className="xt-stat-cell">
          <div className="l">Total Supply</div>
          <div className="v">{totalSupplyDisplay}</div>
        </div>
      </div>

      {/* CONTRACT ADDRESS (XT only) */}
      <div className="xt-contract">
        <div className="head">
          <div className="head-icon">🔗</div>
          <h3>XT Contract Address</h3>
        </div>
        <div className="sub">Verify on-chain. Always double-check the address before importing to your wallet.</div>
        <div className="xt-addr-box">
          <div className="addr">
            {isZeroAddr ? <span style={{ color: 'var(--warning)' }}>Not deployed yet</span> : CONTRACTS.XT}
          </div>
          <button className="btn-icon" onClick={copyAddr} disabled={isZeroAddr} title="Copy">
            {copied ? '✓' : '⎘'}
          </button>
          <a
            className="btn-icon"
            href={isZeroAddr ? '#' : `${CURRENT_NETWORK.blockExplorer}/token/${CONTRACTS.XT}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => isZeroAddr && e.preventDefault()}
            title="View on explorer"
            style={isZeroAddr ? { pointerEvents: 'none', opacity: 0.4 } : {}}
          >
            ↗
          </a>
        </div>
      </div>

      {/* ADD TO WALLET */}
      <div className="add-wallet-card">
        <div className="aw-info">
          <h4>💼 Add XT to your Wallet</h4>
          <p>One-tap add — see your XT balance directly in Trust Wallet, MetaMask, or any Web3 wallet.</p>
        </div>
        <button className="btn btn-primary" onClick={addTokenToWallet} disabled={!isConnected || isZeroAddr}>
          + Add XT Token
        </button>
      </div>

      {/* UTILITY */}
      <div className="xt-section">
        <div className="xt-section-head">
          <div className="xt-section-icon">⚙</div>
          <div>
            <h2>Token Utility</h2>
            <div className="head-sub">What XT does inside the X-Terra ecosystem</div>
          </div>
        </div>
        <div className="utility-grid">
          {UTILITY.map((u, i) => (
            <div key={i} className="utility-item">
              <div className="u-icon">{u.icon}</div>
              <h4>{u.title}</h4>
              <p>{u.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* TOKENOMICS */}
      <div className="xt-section">
        <div className="xt-section-head">
          <div className="xt-section-icon">📊</div>
          <div>
            <h2>Tokenomics</h2>
            <div className="head-sub">The core parameters of XT</div>
          </div>
        </div>
        <div className="tokenomics-grid">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              width: 200, height: 200, borderRadius: '50%',
              background: `conic-gradient(var(--accent) 0deg 216deg, #3B9BFF 216deg 288deg, #B77DFF 288deg 360deg)`,
              padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 30px var(--accent-glow)',
            }}>
              <div style={{
                width: '100%', height: '100%', borderRadius: '50%',
                background: 'var(--bg-card)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column',
              }}>
                <div style={{ fontSize: 30, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>XT</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, letterSpacing: '0.15em' }}>
                  MINING · REWARDS · UTILITY
                </div>
              </div>
            </div>
          </div>
          <div className="tokenomics-list">
            {TOKENOMICS.map((t, i) => (
              <div key={i} className="tokenomics-row">
                <span className="l">{t.label}</span>
                <span className="r">{t.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW TO EARN */}
      <div className="xt-section">
        <div className="xt-section-head">
          <div className="xt-section-icon">⛏</div>
          <div>
            <h2>How to Earn XT</h2>
            <div className="head-sub">Six simple steps to start mining</div>
          </div>
        </div>
        <div className="earn-list">
          {HOW_TO_EARN.map((step, i) => (
            <div key={i} className="earn-step">
              <div>
                <h4>{step.title}</h4>
                <p>{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* WHITEPAPER */}
      <PdfCard
        icon="📄"
        title="X-Terra Whitepaper"
        desc="Full technical & tokenomics documentation. Read before investing."
        url={WHITEPAPER_URL}
        readLabel="📖 Read Whitepaper"
        notify={notify}
      />

      {/* BUSINESS PLAN */}
      <PdfCard
        icon="📘"
        title="X-Terra Business Plan"
        desc="Detailed compensation, machine tiers, and rollout plan."
        url={PLAN_URL}
        readLabel="📖 Read Plan"
        notify={notify}
      />

      {/* ROADMAP */}
      <div className="xt-section">
        <div className="xt-section-head">
          <div className="xt-section-icon">🗺</div>
          <div>
            <h2>Roadmap</h2>
            <div className="head-sub">Where XT is heading</div>
          </div>
        </div>
        <div className="roadmap">
          {ROADMAP.map((r, i) => (
            <div key={i} className={`roadmap-item ${r.status}`}>
              <div className="roadmap-phase">{r.phase}</div>
              <h4>{r.title}</h4>
              <p>{r.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="xt-section">
        <div className="xt-section-head">
          <div className="xt-section-icon">❓</div>
          <div>
            <h2>Frequently Asked</h2>
            <div className="head-sub">Common questions about XT</div>
          </div>
        </div>
        <div>
          {FAQ.map((item, i) => (
            <div
              key={i}
              className={`faq-item ${openFaq === i ? 'open' : ''}`}
              onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
            >
              <div className="q">
                <span>{item.q}</span>
                <span className="toggle">+</span>
              </div>
              <div className="a">{item.a}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------------- PDF card (dapp-browser safe) ---------------- */

// Detect a dapp/webview browser (Trust, MetaMask, Binance, generic wv, etc.).
// These sandboxes silently block <a download> and script-triggered file saves,
// so we always open in a new tab and offer a copy-link fallback.
function isDappBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Trust|MetaMask|BitKeep|SafePal|Binance|imToken|TokenPocket|Coin98|OKApp|Bybit|1inch|Rainbow|Zerion|Coinbase|WebView|; wv\)/i
    .test(ua) || !!window.ethereum?.isTrust || !!window.ethereum?.isMetaMask;
}

function PdfCard({ icon, title, desc, url, readLabel, notify }) {
  // Absolute URL so it survives being copied out to another browser.
  const absoluteUrl = typeof window !== 'undefined'
    ? new URL(url, window.location.origin).toString()
    : url;

  function openPdf(e) {
    e?.preventDefault?.();
    // window.open reliably escapes the SPA and gives the user the browser's
    // built-in save/share toolbar. Falls back to location change if popups blocked.
    const w = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    if (!w) window.location.href = absoluteUrl;
  }

  function copyLink() {
    const clip = navigator.clipboard;
    if (clip && clip.writeText) {
      clip.writeText(absoluteUrl)
        .then(() => notify?.success?.('Link copied — paste it in your browser to download.'))
        .catch(() => notify?.error?.('Could not copy. Long-press the Open button and choose "Copy link".'));
    } else {
      notify?.info?.(absoluteUrl);
    }
  }

  const inDapp = isDappBrowser();

  return (
    <div className="whitepaper-card">
      <div className="wp-icon">{icon}</div>
      <div className="wp-info">
        <h3>{title}</h3>
        <p>{desc}</p>
        {inDapp && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            Tip: in-app wallet browsers may block direct downloads. Open the PDF, then use the browser
            menu (⋮ / share icon) to save — or copy the link and open it in Chrome/Safari.
          </p>
        )}
      </div>
      <div className="wp-actions">
        <button className="btn btn-primary" onClick={openPdf}>
          {readLabel}
        </button>
        <button className="btn btn-secondary" onClick={copyLink}>
          🔗 Copy Link
        </button>
      </div>
    </div>
  );
}
